import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Plus, Target, Zap, Brain, Dumbbell, Star, Trash2, Eye } from "lucide-react";
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer } from 'recharts';
import { toast } from "sonner";
import { useTheme, useThemeClasses } from "../hooks/useTheme";
import { useUser } from "../context/UserContext";
import { API } from "../utils/constants";

export const MyGoalPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isDark } = useTheme();
  const theme = useThemeClasses(isDark);
  const { user, isCoach } = useUser();
  const [goals, setGoals] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingGoalId, setEditingGoalId] = useState(null);
  const [editDescription, setEditDescription] = useState("");
  const [selection, setSelectedGoal] = useState(null);
  const athleteId = searchParams.get('athlete_id');
  const targetGoalId = searchParams.get('goal_id');
  const targetReviewId = searchParams.get('review_id');
  const sourceActivityId = !isCoach ? searchParams.get('activity_id') : null;
  const sourceActivityName = searchParams.get('activity_name') || '';
  const [creatingGoal, setCreatingGoal] = useState(false);
  useEffect(() => { if (sourceActivityId) setShowForm(true); }, [sourceActivityId]);
  const scopeKey = `${user?.id}|${isCoach}|${athleteId || ''}|${targetGoalId || ''}|${targetReviewId || ''}`;
  const currentScope = useRef(scopeKey); currentScope.current = scopeKey;
  const [loadedScope, setLoadedScope] = useState(null);
  const selectedGoal = loadedScope === scopeKey ? selection : null;
  const [loadError, setLoadError] = useState('');
  const [reviewError, setReviewError] = useState(''); const reviewRequest = useRef(null);
  const [readError, setReadError] = useState(''); const [readRetry, setReadRetry] = useState(0);
  const [newGoal, setNewGoal] = useState({ title: '', description: '', category: 'technical', target_rating: 8 });
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewRating, setReviewRating] = useState(7);
  const [reviewComment, setReviewComment] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);
  
  const activityGoals = JSON.parse(localStorage.getItem('mygoal-activity-goals') || '{}');

  const categories = [
    { value: 'technical', label: 'Technical', icon: Eye, color: 'purple' },
    { value: 'physical', label: 'Physical', icon: Zap, color: 'green' },
    { value: 'tactical', label: 'Tactical', icon: Brain, color: 'blue' },
    { value: 'mental', label: 'Mental', icon: Target, color: 'orange' }
  ];

  // Category color mapping - matching TeamGoalsPage style
  const getCategoryColors = (category) => {
    const colors = {
      technical: { 
        bg: isDark ? 'bg-purple-500/20' : 'bg-purple-100', 
        text: isDark ? 'text-purple-400' : 'text-purple-600',
        border: isDark ? 'border-purple-500/30' : 'border-purple-200',
        gradient: 'from-purple-500 to-purple-600',
        icon: isDark ? 'text-purple-400' : 'text-purple-500'
      },
      physical: { 
        bg: isDark ? 'bg-green-500/20' : 'bg-green-100', 
        text: isDark ? 'text-green-400' : 'text-green-600',
        border: isDark ? 'border-green-500/30' : 'border-green-200',
        gradient: 'from-green-500 to-green-600',
        icon: isDark ? 'text-green-400' : 'text-green-500'
      },
      tactical: { 
        bg: isDark ? 'bg-blue-500/20' : 'bg-blue-100', 
        text: isDark ? 'text-blue-400' : 'text-blue-600',
        border: isDark ? 'border-blue-500/30' : 'border-blue-200',
        gradient: 'from-blue-500 to-blue-600',
        icon: isDark ? 'text-blue-400' : 'text-blue-500'
      },
      mental: { 
        bg: isDark ? 'bg-orange-500/20' : 'bg-orange-100', 
        text: isDark ? 'text-orange-400' : 'text-orange-600',
        border: isDark ? 'border-orange-500/30' : 'border-orange-200',
        gradient: 'from-orange-500 to-orange-600',
        icon: isDark ? 'text-orange-400' : 'text-orange-500'
      }
    };
    return colors[category] || colors.technical;
  };

  const fetchData = useCallback(async () => {
    if (!user?.id) return;
    try {
      if (isCoach && !athleteId) throw new Error('Choose a participant from Team Goals to view their goals.');
      const goalsUrl = isCoach ? `${API}/athlete/${athleteId}/goals` : `${API}/goals?user_id=${user.id}`;
      // A coach can read goals and send feedback, but cannot read private self-ratings.
      const [goalsRes, sessionsRes] = await Promise.all([axios.get(goalsUrl), isCoach ? Promise.resolve({ data: [] }) : axios.get(`${API}/sessions?user_id=${user.id}`)]);
      const rows = goalsRes.data;
      if (targetGoalId && !rows.some(g => g.id === targetGoalId)) {
        if (isCoach) throw new Error('This goal is unavailable.');
        const { data } = await axios.get(`${API}/goals/${targetGoalId}`);
        rows.push(data);
      }
      if (currentScope.current !== scopeKey) return;
      setGoals(rows); setSessions(sessionsRes.data); setLoadedScope(scopeKey); setLoadError('');
      setSelectedGoal(current => rows.find(g => g.id === (targetGoalId || current?.id)) || null);
    } catch (err) {
      if (currentScope.current !== scopeKey) return;
      setGoals([]); setSessions([]); setSelectedGoal(null); setLoadedScope(scopeKey);
      setLoadError(isCoach && !athleteId ? 'Choose a participant from Team Goals to view their goals.' : 'Could not load this goal. It may have been removed or access may have changed. Please retry.');
    } finally { if (currentScope.current === scopeKey) setLoading(false); }
  }, [athleteId, targetGoalId, user?.id, isCoach, scopeKey]);

  useEffect(() => { setLoading(true); setSelectedGoal(null); setShowReviewForm(false); setSubmittingReview(false); setReviewRating(7); setReviewComment(''); setReviewError(''); setReadError(''); reviewRequest.current = null; fetchData(); }, [fetchData]);

  const pendingReviews = (selectedGoal?.unread_coach_review_ids || []).filter(id => selectedGoal.coach_reviews?.some(r => r.id === id));
  const pendingKey = pendingReviews.join(',');
  useEffect(() => {
    if (isCoach || !selectedGoal?.id || !pendingKey) return;
    let active = true; const ids = pendingKey.split(','); const goalId = selectedGoal.id;
    setReadError('');
    axios.post(`${API}/goals/${goalId}/reviews/read`, { review_ids: ids }).then(() => {
      if (!active || currentScope.current !== scopeKey) return;
      const update = goal => ({ ...goal, unread_coach_review_ids: (goal.unread_coach_review_ids || []).filter(id => !ids.includes(id)) });
      setGoals(current => current.map(g => g.id === goalId ? update(g) : g));
      setSelectedGoal(current => current?.id === goalId ? update(current) : current);
      window.dispatchEvent(new Event('mygoal-notifications-changed'));
    }).catch(() => { if (active) setReadError('Feedback is visible, but its read status could not be saved.'); });
    return () => { active = false; };
  }, [isCoach, selectedGoal?.id, pendingKey, readRetry, scopeKey]);
  const selectedGoalId = selectedGoal?.id;
  useEffect(() => {
    if (!selectedGoalId || !targetGoalId) return;
    const element = document.getElementById(`coach-review-${targetReviewId}`) || document.getElementById('coach-feedback');
    element?.scrollIntoView?.({ block: 'center', behavior: 'smooth' }); element?.focus?.({ preventScroll: true });
  }, [selectedGoalId, targetGoalId, targetReviewId]);

  const getSessionsForGoal = (goalId) => {
    const sessionsWithGoal = [];
    Object.entries(activityGoals).forEach(([activityId, assignedGoals]) => {
      if (assignedGoals.some(g => g.id === goalId)) {
        const activityTitles = { 'yesterday': 'Team Practice', 'twodaysago': 'Cardio Session', 'threedaysago': 'Match vs City FC', 'tomorrow': 'Strength Training', 'day2': 'Match vs FC United', 'day3': 'Recovery Session', 'day5': 'Tactical Training' };
        const sessionName = activityTitles[activityId];
        const session = sessions.find(s => s.name === sessionName && s.rating);
        if (session) sessionsWithGoal.push(session);
      }
    });
    return sessionsWithGoal;
  };

  const getGoalAverageRating = (goal) => {
    if (!goal.ratings || goal.ratings.length === 0) return 0;
    const sum = goal.ratings.reduce((acc, r) => acc + r.rating, 0);
    return (sum / goal.ratings.length).toFixed(1);
  };

  const handleAddGoal = async () => {
    if (creatingGoal) return;
    if (!newGoal.title.trim()) { toast.error("Please enter a goal title"); return; }
    if (!user?.id) { toast.error("You must be logged in to create a goal"); return; }
    setCreatingGoal(true);
    try {
      await axios.post(`${API}/goals?user_id=${user.id}`, { ...newGoal,
        ...(sourceActivityId ? { activity_id: sourceActivityId, activity_name: sourceActivityName } : {}) });
      toast.success("Goal created!");
      setNewGoal({ title: '', description: '', category: 'technical', target_rating: 8 });
      setShowForm(false);
      window.dispatchEvent(new Event('mygoal-activities-updated'));
      if (sourceActivityId) {
        navigate(`/agenda?activity_id=${encodeURIComponent(sourceActivityId)}&action=goal`);
        return;
      }
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to create goal"); }
    finally { setCreatingGoal(false); }
  };

  const handleUpdateDescription = async (goalId) => {
    try {
      await axios.put(`${API}/goals/${goalId}`, { description: editDescription });
      toast.success("Goal updated!");
      setEditingGoalId(null);
      setEditDescription("");
      fetchData();
    } catch (e) { toast.error("Failed to update goal"); }
  };

  const handleDeleteGoal = async (goalId) => {
    if (!window.confirm("Delete this goal?")) return;
    try {
      await axios.delete(`${API}/goals/${goalId}`);
      toast.success("Goal deleted");
      setSelectedGoal(null);
      fetchData();
    } catch (e) { toast.error("Failed to delete goal"); }
  };

  const handleSubmitReview = async () => {
    if (submittingReview || !selectedGoal) return;
    if (!reviewComment.trim()) { setReviewError('Please add feedback before sending.'); return; }
    const goalId = selectedGoal.id;
    reviewRequest.current ||= crypto.randomUUID();
    setSubmittingReview(true); setReviewError('');
    try {
      const { data } = await axios.post(`${API}/goals/${goalId}/review`, { rating: reviewRating, comment: reviewComment.trim(), request_id: reviewRequest.current }, { timeout: 15000 });
      if (currentScope.current !== scopeKey) return;
      setGoals(current => current.map(g => g.id === goalId ? data : g));
      setSelectedGoal(current => current?.id === goalId ? data : current);
      setShowReviewForm(false); setReviewRating(7); setReviewComment(''); reviewRequest.current = null;
      toast.success('Feedback saved and sent to the participant');
    } catch (err) {
      if (currentScope.current === scopeKey) setReviewError(typeof err.response?.data?.detail === 'string' ? err.response.data.detail : 'Could not send feedback. Your text is kept; please retry.');
    } finally { if (currentScope.current === scopeKey) setSubmittingReview(false); }
  };

  const getRadarDataForGoal = (goal) => {
    const goalSessions = getSessionsForGoal(goal.id);
    if (goalSessions.length === 0) return null;
    const avgRatings = goalSessions.reduce((acc, s) => ({ fun: acc.fun + s.rating.fun, learning: acc.learning + s.rating.learning, mygoal: acc.mygoal + s.rating.mygoal, intensity: acc.intensity + s.rating.intensity }), { fun: 0, learning: 0, mygoal: 0, intensity: 0 });
    const count = goalSessions.length;
    return [
      { subject: 'Fun', A: avgRatings.fun / count, fullMark: 10 },
      { subject: 'Learning', A: avgRatings.learning / count, fullMark: 10 },
      { subject: 'MyGoal', A: avgRatings.mygoal / count, fullMark: 10 },
      { subject: 'Intensity', A: avgRatings.intensity / count, fullMark: 10 }
    ];
  };

  const groupedGoals = goals.reduce((acc, goal) => {
    const cat = goal.category || 'technical';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(goal);
    return acc;
  }, {});

  return (
    <div className={`min-h-screen ${theme.bg} flex flex-col transition-colors duration-300`} data-testid="page-mygoal">
      <header className="flex items-center justify-between p-4 pt-6">
        <button data-testid="back-button" disabled={submittingReview} onClick={() => { if (selectedGoal) { setSelectedGoal(null); setShowReviewForm(false); if (targetGoalId) navigate(isCoach ? `/mygoal?athlete_id=${athleteId}` : '/mygoal'); } else navigate('/'); }} className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${theme.button}`}>
          <ArrowLeft className={`w-5 h-5 ${theme.textPrimary}`} />
        </button>
        <h1 className={`text-xl font-bold ${theme.textPrimary}`}>{selectedGoal ? 'Goal Details' : 'My Goals'}</h1>
        {!selectedGoal && !isCoach && (
          <button onClick={() => setShowForm(!showForm)} className="w-10 h-10 rounded-full flex items-center justify-center bg-gradient-to-r from-[#493986] to-[#92a7e8]">
            <Plus className="w-5 h-5 text-white" />
          </button>
        )}
        {(selectedGoal || isCoach) && <div className="w-10" />}
      </header>

      {loadError && <div className="mx-4 mb-4 rounded-2xl border border-red-400/20 p-4"><p role="alert" className="text-sm text-red-400">{loadError}</p><div className="mt-3 flex gap-3"><button type="button" onClick={fetchData} className="text-sm text-violet-400">Retry</button>{isCoach && <button type="button" onClick={() => navigate('/team/goals')} className="text-sm text-violet-400">Team Goals</button>}</div></div>}
      {/* Goals List View */}
      {!selectedGoal && (
        <>
          {showForm && !isCoach && (
            <div className={`mx-4 p-4 rounded-2xl ${theme.card} border backdrop-blur-xl mb-4`}>
              <h3 className={`font-semibold mb-4 ${theme.textPrimary}`}>Create Goal</h3>
              {sourceActivityId && <p className={`mb-3 text-sm ${theme.textMuted}`}>For {sourceActivityName || 'your upcoming activity'}</p>}
              <input type="text" placeholder="e.g., Scanning before passing" value={newGoal.title} onChange={(e) => setNewGoal({...newGoal, title: e.target.value})} className={`w-full px-4 py-3 rounded-xl border mb-3 ${theme.input}`} />
              <textarea placeholder="Explain your goal..." value={newGoal.description} onChange={(e) => setNewGoal({...newGoal, description: e.target.value})} className={`w-full px-4 py-3 rounded-xl border mb-3 ${theme.input}`} rows={3} />
              <div className="grid grid-cols-2 gap-2 mb-4">
                {categories.map(cat => {
                  const Icon = cat.icon;
                  const colors = getCategoryColors(cat.value);
                  const isSelected = newGoal.category === cat.value;
                  return (
                    <button 
                      key={cat.value} 
                      onClick={() => setNewGoal({...newGoal, category: cat.value})} 
                      className={`p-3 rounded-xl border flex items-center gap-3 transition-all ${
                        isSelected 
                          ? `bg-gradient-to-r ${colors.gradient} border-transparent text-white` 
                          : `${colors.bg} ${colors.border} border`
                      }`}
                    >
                      <Icon className={`w-5 h-5 ${isSelected ? 'text-white' : colors.icon}`} />
                      <span className={`text-sm font-medium ${isSelected ? 'text-white' : colors.text}`}>{cat.label}</span>
                    </button>
                  );
                })}
              </div>
              <button onClick={handleAddGoal} disabled={creatingGoal} className="disabled:opacity-50 w-full py-3 bg-gradient-to-r from-[#493986] to-[#92a7e8] text-white font-bold rounded-xl">{creatingGoal ? 'Saving…' : 'Create Goal'}</button>
            </div>
          )}

          {/* Only show goals list when form is NOT open */}
          {!showForm && (
            <div className="flex-1 px-4 pb-8 overflow-y-auto">
              {loading || loadedScope !== scopeKey ? (
                <div className={`text-center py-8 ${theme.textMuted}`}>Loading...</div>
              ) : goals.length === 0 ? (
                <div className={`text-center py-12`}>
                  <Target className={`w-16 h-16 mx-auto mb-4 ${theme.textMuted}`} />
                  <h3 className={`font-semibold mb-2 ${theme.textPrimary}`}>No goals yet</h3>
                  <p className={`text-sm mb-4 ${theme.textMuted}`}>{isCoach ? 'This athlete has no goals' : 'Create your first training goal'}</p>
                  {!isCoach && <button onClick={() => setShowForm(true)} className="px-6 py-3 bg-gradient-to-r from-[#493986] to-[#92a7e8] text-white font-bold rounded-xl">Create Goal</button>}
                </div>
              ) : (
              <div className="space-y-6">
                {categories.map(category => {
                  const categoryGoals = groupedGoals[category.value] || [];
                  if (categoryGoals.length === 0) return null;
                  const CategoryIcon = category.icon;
                  const colors = getCategoryColors(category.value);
                  
                  return (
                    <div key={category.value}>
                      {/* Category Header */}
                      <div className={`flex items-center gap-3 mb-3 p-3 rounded-xl ${colors.bg}`}>
                        <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${colors.gradient} flex items-center justify-center`}>
                          <CategoryIcon className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex-1">
                          <h2 className={`text-lg font-bold ${colors.text}`}>{category.label}</h2>
                          <p className={`text-xs ${theme.textMuted}`}>{categoryGoals.length} goal{categoryGoals.length !== 1 ? 's' : ''}</p>
                        </div>
                      </div>
                      
                      {/* Goals Cards */}
                      <div className="space-y-3">
                        {categoryGoals.map(goal => {
                          const avgRating = getGoalAverageRating(goal);
                          const isEditing = editingGoalId === goal.id;
                          const hasRatings = goal.ratings && goal.ratings.length > 0;
                          
                          return (
                            <div 
                              key={goal.id} 
                              onClick={() => !isEditing && setSelectedGoal(goal)} 
                              className={`p-4 rounded-2xl ${theme.card} border-l-4 ${colors.border.replace('border-', 'border-l-')} backdrop-blur-xl cursor-pointer hover:scale-[1.01] transition-all`} 
                              data-testid={`goal-${goal.id}`}
                            >
                              {/* Goal Header with Title and Rating */}
                              <div className="flex items-start justify-between mb-3">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${colors.bg} ${colors.text}`}>
                                      {category.label}
                                    </span>
                                  </div>
                                  <h3 className={`font-semibold ${theme.textPrimary}`}>{goal.title}</h3>
                                </div>
                                
                                {/* Average Rating Badge */}
                                <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl ${
                                  hasRatings 
                                    ? (isDark ? 'bg-yellow-500/20' : 'bg-yellow-50') 
                                    : (isDark ? 'bg-white/5' : 'bg-gray-100')
                                }`}>
                                  <Star className={`w-4 h-4 ${hasRatings ? 'text-yellow-500 fill-yellow-500' : theme.textMuted}`} />
                                  <span className={`text-sm font-bold ${hasRatings ? 'text-yellow-500' : theme.textMuted}`}>
                                    {avgRating > 0 ? avgRating : '-'}
                                  </span>
                                  {hasRatings && (
                                    <span className={`text-xs ${theme.textMuted}`}>
                                      ({goal.ratings.length})
                                    </span>
                                  )}
                                </div>
                              </div>

                              {/* Rating Progress Bar */}
                              {hasRatings && (
                                <div className="mb-3">
                                  <div className={`h-2 rounded-full ${isDark ? 'bg-white/10' : 'bg-gray-200'}`}>
                                    <div 
                                      className={`h-full rounded-full bg-gradient-to-r ${colors.gradient}`}
                                      style={{ width: `${(parseFloat(avgRating) / 10) * 100}%` }}
                                    />
                                  </div>
                                  <div className="flex justify-between mt-1">
                                    <span className={`text-xs ${theme.textMuted}`}>Progress</span>
                                    <span className={`text-xs font-medium ${colors.text}`}>{avgRating}/10</span>
                                  </div>
                                </div>
                              )}
                              
                              {/* Description Section */}
                              {isEditing && !isCoach ? (
                                <div className={`p-3 rounded-xl ${isDark ? 'bg-white/5' : 'bg-[#493986]/5'}`} onClick={(e) => e.stopPropagation()}>
                                  <label className={`text-xs font-medium ${theme.textMuted} mb-2 block`}>Explanation</label>
                                  <textarea value={editDescription} onChange={(e) => setEditDescription(e.target.value)} placeholder="Explain your goal..." className={`w-full px-3 py-2 rounded-lg border text-sm ${theme.input}`} rows={4} autoFocus />
                                  <div className="flex gap-2 mt-3">
                                    <button onClick={(e) => { e.stopPropagation(); handleUpdateDescription(goal.id); }} className="flex-1 py-2 bg-gradient-to-r from-[#493986] to-[#92a7e8] text-white font-medium rounded-lg text-sm">Save</button>
                                    <button onClick={(e) => { e.stopPropagation(); setEditingGoalId(null); setEditDescription(""); }} className={`px-4 py-2 rounded-lg text-sm ${theme.button} ${theme.textSecondary}`}>Cancel</button>
                                  </div>
                                </div>
                              ) : (
                                <div 
                                  onClick={(e) => { if (!isCoach) { e.stopPropagation(); setEditingGoalId(goal.id); setEditDescription(goal.description || ""); }}} 
                                  className={`p-3 rounded-xl transition-all ${isDark ? 'bg-white/5' : 'bg-[#493986]/5'} ${!isCoach ? (isDark ? 'hover:bg-white/10' : 'hover:bg-[#493986]/10') : ''}`}
                                >
                                  <label className={`text-xs font-medium ${theme.textMuted} mb-1 block`}>Explanation</label>
                                  {goal.description ? (
                                    <p className={`text-sm ${theme.textSecondary}`}>{goal.description}</p>
                                  ) : (
                                    <p className={`text-sm italic ${theme.textMuted}`}>{isCoach ? 'No explanation provided' : 'Tap to add an explanation...'}</p>
                                  )}
                                </div>
                              )}
                              
                              {!isCoach && goal.unread_coach_review_ids?.length > 0 && <p className="mt-3 rounded-xl bg-violet-500/15 px-3 py-2 text-sm font-semibold text-violet-400">{goal.unread_coach_review_ids.length} new coach feedback · Tap to view</p>}
                              {/* Reviews Count */}
                              {goal.coach_reviews && goal.coach_reviews.length > 0 && (
                                <div className={`mt-3 pt-3 border-t ${isDark ? 'border-white/10' : 'border-[#493986]/10'} flex items-center justify-between`}>
                                  <span className={`text-xs ${theme.textMuted}`}>
                                    {goal.coach_reviews.length} coach review{goal.coach_reviews.length !== 1 ? 's' : ''}
                                  </span>
                                  <span className={`text-xs ${colors.text}`}>View details →</span>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          )}
        </>
      )}

      {/* Goal Detail View */}
      {selectedGoal && (
        <div className="flex-1 px-4 pb-8 overflow-y-auto">
          {(() => {
            const colors = getCategoryColors(selectedGoal.category);
            const avgRating = getGoalAverageRating(selectedGoal);
            const hasRatings = selectedGoal.ratings && selectedGoal.ratings.length > 0;
            
            return (
              <>
                <div className={`p-4 rounded-2xl ${theme.card} border backdrop-blur-xl mb-4`}>
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <span className={`text-xs px-3 py-1 rounded-full font-medium ${colors.bg} ${colors.text}`}>
                        {selectedGoal.category}
                      </span>
                      <h2 className={`text-xl font-bold mt-2 ${theme.textPrimary}`}>{selectedGoal.title}</h2>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className={`flex items-center gap-1.5 px-3 py-2 rounded-xl ${hasRatings ? (isDark ? 'bg-yellow-500/20' : 'bg-yellow-50') : (isDark ? 'bg-white/5' : 'bg-gray-100')}`}>
                        <Star className={`w-5 h-5 ${hasRatings ? 'text-yellow-500 fill-yellow-500' : theme.textMuted}`} />
                        <span className={`text-lg font-bold ${hasRatings ? 'text-yellow-500' : theme.textMuted}`}>{avgRating || '-'}</span>
                      </div>
                      {!isCoach && (
                        <button onClick={() => handleDeleteGoal(selectedGoal.id)} className={`w-10 h-10 rounded-xl ${theme.button} flex items-center justify-center`}>
                          <Trash2 className={`w-5 h-5 ${theme.textMuted}`} />
                        </button>
                      )}
                    </div>
                  </div>
                  
                  {/* Progress Bar in Detail View */}
                  {hasRatings && (
                    <div className="mb-4">
                      <div className={`h-3 rounded-full ${isDark ? 'bg-white/10' : 'bg-gray-200'}`}>
                        <div 
                          className={`h-full rounded-full bg-gradient-to-r ${colors.gradient}`}
                          style={{ width: `${(parseFloat(avgRating) / 10) * 100}%` }}
                        />
                      </div>
                      <div className="flex justify-between mt-1">
                        <span className={`text-xs ${theme.textMuted}`}>Average from {selectedGoal.ratings.length} rating{selectedGoal.ratings.length !== 1 ? 's' : ''}</span>
                        <span className={`text-xs font-medium ${colors.text}`}>{avgRating}/10</span>
                      </div>
                    </div>
                  )}
                  
                  {selectedGoal.description && <p className={`${theme.textSecondary}`}>{selectedGoal.description}</p>}
                </div>

                {getRadarDataForGoal(selectedGoal) && (
                  <div className={`p-4 rounded-2xl ${theme.card} border backdrop-blur-xl mb-4`}>
                    <h3 className={`font-semibold mb-3 ${theme.textPrimary}`}>Training Sessions</h3>
                    <ResponsiveContainer width="100%" height={200}>
                      <RadarChart data={getRadarDataForGoal(selectedGoal)}>
                        <PolarGrid stroke={isDark ? 'rgba(255,255,255,0.1)' : 'rgba(73,57,134,0.1)'} />
                        <PolarAngleAxis dataKey="subject" tick={{ fill: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(73,57,134,0.6)', fontSize: 12 }} />
                        <PolarRadiusAxis domain={[0, 10]} tick={{ fill: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(73,57,134,0.4)', fontSize: 10 }} />
                        <Radar name="Rating" dataKey="A" stroke="#493986" fill="#493986" fillOpacity={0.5} />
                      </RadarChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {/* Coach Reviews Section */}
                <div id="coach-feedback" tabIndex={-1} className={`p-4 rounded-2xl ${theme.card} border backdrop-blur-xl`}>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className={`font-semibold ${theme.textPrimary}`}>Coach feedback</h3>
                    {isCoach && !showReviewForm && (
                      <button onClick={() => { setShowReviewForm(true); setReviewError(''); reviewRequest.current = null; }} className="px-3 py-1.5 bg-gradient-to-r from-[#493986] to-[#92a7e8] text-white text-sm font-medium rounded-lg" data-testid="add-review-btn">Add feedback</button>
                    )}
                  </div>

                  {showReviewForm && (
                    <div className={`p-4 rounded-xl ${isDark ? 'bg-white/5' : 'bg-[#493986]/5'} mb-4`}>
                      <label className={`text-sm font-medium ${theme.textSecondary} mb-2 block`}>Rating</label>
                      <input type="range" aria-label="Feedback rating" min="1" max="10" value={reviewRating} disabled={submittingReview} onChange={(e) => { setReviewRating(parseInt(e.target.value)); reviewRequest.current = null; }} className="w-full mb-2" />
                      <div className="flex justify-between text-xs mb-4"><span className={theme.textMuted}>1</span><span className={`font-bold ${theme.textPrimary}`}>{reviewRating}</span><span className={theme.textMuted}>10</span></div>
                      <label className={`text-sm font-medium ${theme.textSecondary} mb-2 block`}>Comment</label>
                      <textarea aria-label="Feedback comment" value={reviewComment} disabled={submittingReview} maxLength={5000} onChange={(e) => { setReviewComment(e.target.value); reviewRequest.current = null; }} placeholder="Add your feedback..." className={`w-full px-3 py-2 rounded-lg border text-sm ${theme.input}`} rows={3} data-testid="review-comment-input" />
                      {reviewError && <p role="alert" className="mt-2 text-sm text-red-400">{reviewError}</p>}
                      <div className="flex gap-2 mt-3">
                        <button onClick={handleSubmitReview} disabled={submittingReview} className="flex-1 py-2 bg-gradient-to-r from-[#493986] to-[#92a7e8] text-white font-medium rounded-lg text-sm disabled:opacity-50" data-testid="submit-review-btn">{submittingReview ? 'Sending…' : 'Send feedback'}</button>
                        <button disabled={submittingReview} onClick={() => { setShowReviewForm(false); setReviewComment(''); setReviewError(''); reviewRequest.current = null; }} className={`px-4 py-2 rounded-lg text-sm ${theme.button} ${theme.textSecondary}`}>Cancel</button>
                      </div>
                    </div>
                  )}

                  {readError && <div className="mb-3"><p role="alert" className="text-sm text-red-400">{readError}</p><button type="button" onClick={() => setReadRetry(value => value + 1)} className="mt-2 text-sm text-violet-400">Retry read status</button></div>}
                  {selectedGoal.coach_reviews && selectedGoal.coach_reviews.length > 0 ? (
                    <div className="space-y-3">
                      {selectedGoal.coach_reviews.map((review, idx) => (
                        <div id={`coach-review-${review.id}`} tabIndex={-1} key={review.id || `${review.coach_id || review.coach_name}-${review.created_at || idx}`} className={`p-3 rounded-xl ${targetReviewId === review.id ? 'ring-2 ring-violet-400/60' : ''} ${isDark ? 'bg-white/5' : 'bg-[#493986]/5'}`}>
                          <div className="flex items-center justify-between mb-2">
                            <span className={`font-medium ${theme.textPrimary}`}>{review.coach_name}</span>
                            <div className="flex items-center gap-1"><Star className="w-4 h-4 text-yellow-500 fill-yellow-500" /><span className={`text-sm font-medium ${theme.textPrimary}`}>{review.rating}</span></div>
                          </div>
                          {!isCoach && selectedGoal.unread_coach_review_ids?.includes(review.id) && <p className="mb-2 text-xs font-semibold text-violet-400">New feedback</p>}
                          <p className={`whitespace-pre-wrap text-sm ${theme.textSecondary}`}>{review.comment}</p>
                          <p className={`text-xs ${theme.textMuted} mt-2`}>{new Date(review.created_at).toLocaleDateString()}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className={`text-center py-4 ${theme.textMuted}`}>{isCoach ? 'No reviews yet. Add your feedback!' : 'No coach reviews yet'}</p>
                  )}
                </div>
              </>
            );
          })()}
        </div>
      )}
    </div>
  );
};

export default MyGoalPage;
